/**
 * Bookerkøen (VIB-3050): hvorfor et lead kommer nå, og reglene i klartekst. Tallene kommer fra
 * lead_rules(), altså de samme verdiene databasen bruker, så teksten følger reglene.
 */
import { formatDateShort, formatDateTimeShort } from "@/lib/format";

export interface LeadRules {
  queue_low: number;
  queue_high: number;
  retry_hours: number;
  same_day_cutoff: number;
  morning_hour: number;
  pool_rest_days: number;
  email_days: number;
  wrong_number_days: number;
  max_attempts: number;
  park_days: number;
  revisit_days: number;
}

export interface QueueReasonInput {
  list_name: string;
  second_try?: boolean;
  call_back_at?: string | null;
  lead: { last_outcome: string | null; last_attempt_at: string | null; attempts_count: number };
}

const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Oslo" });
const hour = (h: number) => `${String(h).padStart(2, "0")}.00`;
const days = (n: number) => (n === 1 ? "1 dag" : `${n} dager`);

/** Én linje øverst på leadkortet: hvorfor leadet kommer nå, og hva som skjer ved neste utfall. */
export function leadQueueReason(p: QueueReasonInput): string {
  const { last_outcome, last_attempt_at, attempts_count } = p.lead;
  if (p.second_try && last_attempt_at) {
    return `Andre forsøk. Du ringte ${clock(last_attempt_at)} uten svar. Får du ikke svar nå, går leadet tilbake til poolen.`;
  }
  if (last_outcome === "call_back") {
    return p.call_back_at ? `Du lovet å ringe tilbake ${formatDateTimeShort(p.call_back_at)}.` : "Du lovet å ringe tilbake.";
  }
  if (last_outcome === "email_sent" && last_attempt_at) {
    return `Oppfølging av e-posten du sendte ${formatDateShort(last_attempt_at)}.`;
  }
  if (attempts_count > 0) {
    return `Forsøk ${attempts_count + 1} fra ${p.list_name}. Leadet har ikke svart før.`;
  }
  return `Nytt lead fra ${p.list_name}.`;
}

/** Reglene for panelet «Slik fordeles leads», nummerert som i wireframen. */
export function leadRulesText(r: LeadRules): { title: string; text: string }[] {
  const revisitMonths = Math.round(r.revisit_days / 30);
  return [
    {
      title: "Køen din",
      text: `Du har mellom ${r.queue_low} og ${r.queue_high} leads klare. Har du færre enn ${r.queue_low} som kan ringes nå, fylles køen opp til ${r.queue_high} fra poolene dine.`,
    },
    {
      title: "Rekkefølge",
      text: "Oppfølgingene dine som har forfalt (ring tilbake og e-post), kommer først. Så andre forsøk, og deretter prioritet A, B og C.",
    },
    {
      title: "Ikke svar første gang",
      text: `Leadet kommer tilbake til deg tidligst ${r.retry_hours} timer senere. Ringer du etter kl. ${hour(r.same_day_cutoff)}, kommer det tilbake neste virkedag kl. ${hour(r.morning_hour)}.`,
    },
    {
      title: "Ikke svar andre gang",
      text: `Leadet går tilbake til poolen og hviler i ${days(r.pool_rest_days)} før en annen booker kan få det.`,
    },
    {
      title: "Ring tilbake og e-post",
      text: `Leadet blir ditt til du booker møte eller avviser det. Det kommer tilbake på tidspunktet du valgte, og ${r.email_days} virkedager etter en e-post. Er du borte, flytter admin det.`,
    },
    {
      title: "Feil nummer",
      text: `Leadet settes til side i ${days(r.wrong_number_days)} mens noen finner et nytt nummer.`,
    },
    {
      title: "Grense",
      text: `Etter ${r.max_attempts} forsøk uten svar, talt for alle bookere, hviler leadet i ${days(r.park_days)}.`,
    },
    {
      title: "Avvist",
      text: `Leadet flyttes til Avviste med årsak og vurderes på nytt etter datoen du velger, som standard ${revisitMonths} måneder.`,
    },
    {
      title: "Påfyll av poolene",
      text: "Poolene fylles der leadene hentes inn. Kommer du under lavvann og poolene dine er tomme, fylles ikke køen før poolen er fylt på.",
    },
    {
      title: "Ingen dobbeltlagring",
      text: "Et selskap ligger bare i én pool. Kunder ringes aldri som kalde leads.",
    },
  ];
}
